import type {
  Broadcast,
  BroadcastChannel,
  CreateBroadcastChannel,
  CreateBroadcastDto,
} from "./use-broadcasts";

/** The API's title limit — the "(copy)" suffix must still fit. */
const MAX_TITLE_LENGTH = 200;
const COPY_SUFFIX = " (copy)";

/**
 * The create payload for a copy of `broadcast`: same projects (or URL), delivery mode, schedule,
 * timezone and channels, named "<title> (copy)" and created **paused** — the same rule as OptiSigns'
 * Duplicate, so a copy never delivers twice before the user has looked at it.
 *
 * The engine echoes each channel's `config` but never its credentials, so every channel is rebuilt
 * from the fields the Edit form already reads back (Slack keeps its Kaze `slackConnectionId`, which
 * the API re-registers). Channel `id`s and the engine's `linkBack` are left for the API to create.
 */
export function duplicateBroadcastDto(
  broadcast: Broadcast,
): CreateBroadcastDto {
  const base = broadcast.title.slice(0, MAX_TITLE_LENGTH - COPY_SUFFIX.length);
  const sources = broadcast.sources?.length ? broadcast.sources : null;
  return {
    title: `${base}${COPY_SUFFIX}`,
    ...(sources
      ? {
          sources: sources.map(({ type, projectId, url }) => ({
            type,
            projectId,
            url,
          })),
        }
      : {
          source: {
            type: broadcast.source.type,
            projectId: broadcast.source.projectId,
            url: broadcast.source.url,
          },
        }),
    ...(broadcast.deliveryMode ? { deliveryMode: broadcast.deliveryMode } : {}),
    cron: broadcast.cron,
    timezone: broadcast.timezone,
    channels: broadcast.channels
      .map(toCreateChannel)
      .filter((c): c is CreateBroadcastChannel => c !== null),
    enabled: false,
  };
}

function toCreateChannel(
  channel: BroadcastChannel,
): CreateBroadcastChannel | null {
  const config = channel.config ?? {};
  const text = (key: string): string | undefined =>
    typeof config[key] === "string" && config[key]
      ? (config[key] as string)
      : undefined;
  switch (channel.type) {
    case "slack":
      return {
        type: "slack",
        slackConnectionId: text("slackConnectionId"),
        channelId: text("channelId"),
        channelName: text("channelName"),
        message: text("message"),
      };
    case "webhook":
      return { type: "webhook", url: text("url") };
    case "teams":
      // Stored as `webhookUrl`; the create payload calls it `url`.
      return {
        type: "teams",
        url: text("webhookUrl") ?? text("url"),
        message: text("message"),
      };
    case "email":
      return {
        type: "email",
        recipients: Array.isArray(config.recipients)
          ? (config.recipients as string[])
          : [],
        subject: text("subject"),
      };
    case "pdf":
      return { type: "pdf" };
    default:
      return null;
  }
}
